import React, { memo, useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useFavorites } from '@/feature/betslip-pickem/components/FavoritesProvider';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { isWeb } from '@/utils/constants-platform-specific';

import { useLobbyData } from '../hooks/LobbyDataProvider';
import { extractPlayersListKey } from '../utils/lobbySections';
import { PlayerSmallCard } from './PlayerSmallCard';
import { PlayerSmallCardSkeleton } from './Skeletons/PlayerSmallCardSkeleton';

const ItemSeparatorComponent = () => <SizedBox value={12} />;
const numberOfSkeletonCards = isWeb ? 10 : 5;

export const FavouritesList = memo(({ title }: { title: string }) => {
    const { favoritePlayers, hasNoData, fetching } = useFavorites();

    const { hasLobbyData } = useLobbyData();

    const data = fetching ? new Array(numberOfSkeletonCards) : favoritePlayers;

    const trackOpenCardEvent = useCallback(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.EXPAND_PLAYER_PROPS, {
            location: 'favorites',
        });
    }, []);

    const renderItem = useCallback(
        ({ item, index }: { item: PlayerWithTeamAndEvent; index: number }) => {
            if (fetching || !hasLobbyData) {
                return (
                    <PlayerSmallCardSkeleton
                        isFirstItem={index === 0}
                        isLastItem={index === numberOfSkeletonCards - 1}
                        showNumber={false}
                    />
                );
            }

            return (
                <PlayerSmallCard
                    player={item}
                    listOfPlayers={favoritePlayers}
                    index={index}
                    showNumber={false}
                    trackEvent={trackOpenCardEvent}
                />
            );
        },
        [fetching, hasLobbyData, favoritePlayers, trackOpenCardEvent]
    );

    if ((!fetching || hasLobbyData) && hasNoData) {
        return null;
    }

    return (
        <Box paddingBottom={'s20'} paddingTop={'s16'} style={styles.container}>
            <Text
                color={ds.colorToken.text.primary}
                variant={'headlineMedium'}
                lineHeight={28}
                marginBottom={'s20'}
                marginLeft={'s16'}
            >
                {title}
            </Text>
            <FlatList
                data={data}
                renderItem={renderItem}
                keyExtractor={(it, index) => extractPlayersListKey(it, index, fetching)}
                horizontal
                showsHorizontalScrollIndicator={false}
                ItemSeparatorComponent={ItemSeparatorComponent}
                extraData={{ fetching, hasLobbyData }}
            />
        </Box>
    );
});

const styles = StyleSheet.create({
    container: {
        borderTopWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
});
